import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  ProgressBar,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  ArrowDownload20Regular,
  ArrowLeft20Regular,
  Delete20Regular,
  Play20Regular,
} from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  useCancelPublishedMediaMetadataMigration,
  usePublishedMediaMetadataMigration,
  usePublishedMediaMetadataMigrationExceptions,
  usePublishedMediaMetadataMigrationWorkerStatus,
  useStartPublishedMediaMetadataMigration,
} from "../hooks/usePublishedMediaMetadataMigration";
import { ClientRoutes } from "../models/ClientRoutes";
import {
  isPublishedMediaMigrationActive,
  PublishedMediaMetadataMigrationStatus,
} from "../models/PublishedMediaMetadataMigration";
import { VsaaColorTokens } from "../style/VsaaColorTokens";

const storageKey = "published-media-metadata-migration-job-id";
const useStyles = makeStyles({
  root: {
    padding: tokens.spacingVerticalL,
    overflowY: "auto",
    width: "100%",
    boxSizing: "border-box",
    minWidth: 0,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalM,
    flexWrap: "wrap",
  },
  actions: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
  panel: {
    marginTop: tokens.spacingVerticalL,
    borderTop: `1px solid ${VsaaColorTokens.color_file_extension_badge_border}`,
    paddingTop: tokens.spacingVerticalL,
  },
  rows: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: tokens.spacingHorizontalL,
  },
  item: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    minWidth: 0,
  },
  value: { overflowWrap: "anywhere" },
  progress: { marginTop: tokens.spacingVerticalL },
  danger: {
    backgroundColor: VsaaColorTokens.color_destructive_button_background,
    color: VsaaColorTokens.color_destructive_button_text,
    ":hover": {
      backgroundColor:
        VsaaColorTokens.color_destructive_button_background_hover,
    },
  },
});

const statusKeys: Record<PublishedMediaMetadataMigrationStatus, string> = {
  0: "pending",
  1: "running",
  2: "completed",
  3: "failed",
  4: "cancelled",
  5: "completedWithExceptions",
  6: "cancelling",
};

type ActionError = {
  key: "startError" | "cancelError" | "downloadError";
  correlationId?: string;
};

function getActionError(
  error: unknown,
  fallbackKey: ActionError["key"]
): ActionError {
  if (
    !error ||
    typeof error !== "object" ||
    !("message" in error) ||
    typeof error.message !== "string"
  ) {
    return { key: fallbackKey };
  }
  try {
    const details = JSON.parse(error.message) as Record<string, unknown>;
    const correlationId =
      typeof details.correlationId === "string" &&
      /^[a-zA-Z0-9-]{1,128}$/.test(details.correlationId)
        ? details.correlationId
        : undefined;
    return {
      key: fallbackKey,
      correlationId,
    };
  } catch {
    return { key: fallbackKey };
  }
}

export default function PublishedMediaMetadataMigrationPage() {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [jobId, setJobId] = useState(
    () => localStorage.getItem(storageKey) ?? undefined
  );
  const [confirmAction, setConfirmAction] = useState<"start" | "cancel" | null>(
    null
  );
  const [actionError, setActionError] = useState<ActionError>();
  const [currentTime, setCurrentTime] = useState(0);
  const statusQuery = usePublishedMediaMetadataMigration(jobId);
  const workerStatusQuery = usePublishedMediaMetadataMigrationWorkerStatus();
  const startMutation = useStartPublishedMediaMetadataMigration();
  const cancelMutation = useCancelPublishedMediaMetadataMigration(jobId);
  const exceptionDownload = usePublishedMediaMetadataMigrationExceptions(jobId);
  const job = statusQuery.data;
  const workerEnabled = workerStatusQuery.data?.workerEnabled !== false;
  const active = isPublishedMediaMigrationActive(job?.status);
  const actionPending =
    startMutation.isPending ||
    cancelMutation.isPending ||
    exceptionDownload.isRefetching;

  useEffect(() => {
    const timer = window.setInterval(() => setCurrentTime(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const start = async (retryExceptions = false, resume = false) => {
    setActionError(undefined);
    try {
      const started = await startMutation.mutateAsync(
        retryExceptions
          ? { resumeJobId: jobId, retryExceptions: true }
          : resume
            ? { resumeJobId: jobId }
            : {}
      );
      localStorage.setItem(storageKey, started.id);
      setJobId(started.id);
    } catch (error) {
      setActionError(getActionError(error, "startError"));
    } finally {
      setConfirmAction(null);
    }
  };

  const cancel = async () => {
    setActionError(undefined);
    try {
      await cancelMutation.mutateAsync();
    } catch (error) {
      setActionError(getActionError(error, "cancelError"));
    } finally {
      setConfirmAction(null);
    }
  };

  const download = async () => {
    setActionError(undefined);
    try {
      const result = await exceptionDownload.refetch();
      if (!result.data) {
        setActionError({ key: "downloadError" });
        return;
      }
      const url = URL.createObjectURL(result.data.blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = result.data.filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setActionError(getActionError(error, "downloadError"));
    }
  };

  const counters = job?.mediaMigrationCounters;
  const heartbeatTime = job?.heartbeatAtUtc
    ? new Date(job.heartbeatAtUtc).getTime()
    : 0;
  const stale = Boolean(
    currentTime && heartbeatTime && currentTime - heartbeatTime > 15_000
  );

  return (
    <main className={classes.root} aria-live="polite">
      <div className={classes.header}>
        <Button
          appearance="subtle"
          icon={<ArrowLeft20Regular />}
          onClick={() => navigate(ClientRoutes.SystemAdmin)}
        >
          {t("publishedMediaMetadataMigration.back")}
        </Button>
        <Text as="h1" size={600} weight="semibold">
          {t("publishedMediaMetadataMigration.title")}
        </Text>
        <div className={classes.actions}>
          {!active && (
            <Button
              appearance="primary"
              icon={<Play20Regular />}
              disabled={actionPending}
              onClick={() => setConfirmAction("start")}
            >
              {t("publishedMediaMetadataMigration.start")}
            </Button>
          )}
          {active && (
            <Button
              className={classes.danger}
              icon={<Delete20Regular />}
              disabled={actionPending}
              onClick={() => setConfirmAction("cancel")}
            >
              {t("publishedMediaMetadataMigration.cancel")}
            </Button>
          )}
          {(counters?.exceptions ?? 0) > 0 && (
            <Button
              icon={<ArrowDownload20Regular />}
              disabled={actionPending}
              onClick={download}
            >
              {t("publishedMediaMetadataMigration.export")}
            </Button>
          )}
          {job?.canRetryExceptions && (
            <Button disabled={actionPending} onClick={() => start(true)}>
              {t("publishedMediaMetadataMigration.retry")}
            </Button>
          )}
          {job?.canResume && !job.canRetryExceptions && !active && (
            <Button disabled={actionPending} onClick={() => start(false, true)}>
              {t("publishedMediaMetadataMigration.resume")}
            </Button>
          )}
        </div>
      </div>

      {!navigator.onLine && (
        <MessageBar intent="warning">
          <MessageBarBody>
            {t("publishedMediaMetadataMigration.offline")}
          </MessageBarBody>
        </MessageBar>
      )}
      {!workerEnabled && (
        <MessageBar intent="warning">
          <MessageBarBody>
            {t("publishedMediaMetadataMigration.workerDisabled")}
          </MessageBarBody>
        </MessageBar>
      )}
      {statusQuery.error && (
        <MessageBar intent="error">
          <MessageBarBody>
            {t("publishedMediaMetadataMigration.loadError")}
          </MessageBarBody>
        </MessageBar>
      )}
      {actionError && (
        <div role="alert">
          <MessageBar intent="error">
            <MessageBarBody>
              {t(`publishedMediaMetadataMigration.${actionError.key}`)}
              {actionError.correlationId && (
                <>
                  {" "}
                  {t("publishedMediaMetadataMigration.correlationId")}{" "}
                  {actionError.correlationId}
                </>
              )}
            </MessageBarBody>
          </MessageBar>
        </div>
      )}
      {!job && statusQuery.isLoading && (
        <div className={classes.panel}>
          <Spinner label={t("publishedMediaMetadataMigration.loading")} />
        </div>
      )}
      {!job && !statusQuery.isLoading && !statusQuery.error && (
        <div className={classes.panel}>
          <Text>{t("publishedMediaMetadataMigration.empty")}</Text>
        </div>
      )}
      {job && (
        <section className={classes.panel}>
          <div className={classes.rows}>
            <div className={classes.item}>
              <Text>{t("publishedMediaMetadataMigration.status")}</Text>
              <Badge>
                {t(
                  `publishedMediaMetadataMigration.states.${statusKeys[job.status]}`
                )}
              </Badge>
            </div>
            <div className={classes.item}>
              <Text>{t("publishedMediaMetadataMigration.jobId")}</Text>
              <Text className={classes.value}>{job.id}</Text>
            </div>
            <div className={classes.item}>
              <Text>{t("publishedMediaMetadataMigration.started")}</Text>
              <Text>{new Date(job.startTime).toLocaleString("sv-SE")}</Text>
            </div>
            <div className={classes.item}>
              <Text>{t("publishedMediaMetadataMigration.heartbeat")}</Text>
              <Text>
                {job.heartbeatAtUtc
                  ? new Date(job.heartbeatAtUtc).toLocaleString("sv-SE")
                  : "-"}
              </Text>
            </div>
            <div className={classes.item}>
              <Text>{t("publishedMediaMetadataMigration.page")}</Text>
              <Text>{job.pageNumber}</Text>
            </div>
            {(
              [
                "scanned",
                "updated",
                "skipped",
                "fileMissing",
                "ambiguous",
                "exceptions",
                "conflicts",
              ] as const
            ).map((counter) => (
              <div className={classes.item} key={counter}>
                <Text>{t(`publishedMediaMetadataMigration.${counter}`)}</Text>
                <Text>{counters?.[counter] ?? 0}</Text>
              </div>
            ))}
            {!active && job.endTime && (
              <div className={classes.item}>
                <Text>{t("publishedMediaMetadataMigration.finished")}</Text>
                <Text>{new Date(job.endTime).toLocaleString("sv-SE")}</Text>
              </div>
            )}
          </div>
          <ProgressBar
            className={classes.progress}
            value={active ? undefined : 1}
            aria-label={t("publishedMediaMetadataMigration.progress")}
          />
          {stale && (
            <MessageBar intent="warning">
              <MessageBarBody>
                {t("publishedMediaMetadataMigration.stale")}
              </MessageBarBody>
            </MessageBar>
          )}
          {job.status === PublishedMediaMetadataMigrationStatus.Failed && (
            <MessageBar intent="error">
              <MessageBarBody>
                {t("publishedMediaMetadataMigration.failedMessage")}
              </MessageBarBody>
            </MessageBar>
          )}
        </section>
      )}

      <Dialog
        open={confirmAction !== null}
        onOpenChange={(_, data) => !data.open && setConfirmAction(null)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {t(
                `publishedMediaMetadataMigration.confirm.${confirmAction ?? "start"}.title`
              )}
            </DialogTitle>
            <DialogContent>
              {t(
                `publishedMediaMetadataMigration.confirm.${confirmAction ?? "start"}.body`
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmAction(null)}>
                {t("publishedMediaMetadataMigration.close")}
              </Button>
              <Button
                appearance="primary"
                disabled={actionPending}
                className={
                  confirmAction === "cancel" ? classes.danger : undefined
                }
                onClick={() =>
                  confirmAction === "cancel" ? cancel() : start()
                }
              >
                {t("publishedMediaMetadataMigration.confirmAction")}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </main>
  );
}
